import {call, put, select, take, takeEvery, takeLatest} from 'redux-saga/effects'

import {
  GET_CURRENT_EVENT_SUCCESS,
  getCheckoutMessageFailure,
  getCheckoutMessagePending,
  getCheckoutMessageSuccess,
} from '../actions/api'
import {proceedToCheckout} from '../actions/checkout'
import {
  CLOSE_DASHBOARD,
  ENTER_EVENT_DETAIL_EDIT_MODE,
  LEAVE_EVENT_DETAIL_EDIT_MODE,
  OPEN_DASHBOARD,
  TOGGLE_DASHBOARD,
  VALIDATE_DASHBOARD,
  VALIDATE_DASHBOARD_FAILURE,
  closeDashboard,
  closeEventDetails,
  leaveEventDetailEditMode,
  openDashboard,
  openEventDetails,
  validateDashboardFailure,
  validateDashboardPending,
  validateDashboardSuccess,
} from '../actions/dashboard'
import {UPDATE_CURRENT_EVENT} from '../actions/events'
import {EDIT_LOOK_NAME_SUCCESS} from '../actions/looks'
import {CLOSE_MOBILE_MENU} from '../actions/mobileMenu'
import {closeSlidingModal} from '../actions/modal'
import {ADD_PARTICIPANT, REMOVE_PARTICIPANT} from '../actions/people'
import * as api from '../api/api'
import {isBadResponse} from '../api/statusCodes'
import {getErrors, scrollToFirstLookError} from '../components/Dashboard/Cta/helpers'
import * as cross from '../store/cross'
import * as dashboard from '../store/dashboard'
import * as events from '../store/events'
import * as looks from '../store/looks'
import * as mobileMenu from '../store/mobileMenu'
import * as track from '../track/track'
import * as URLS from '../urls'
import {formatDateString} from '../utils'

const html = document.documentElement

export function* toggleDashboardWorker() {
  const isOpen = yield select(dashboard.isOpen)
  yield put(isOpen ? closeDashboard() : openDashboard())
}

export function* toggleDashboardSaga() {
  yield takeLatest(TOGGLE_DASHBOARD, toggleDashboardWorker)
}

// Open
// ----------------------------------------------------------------------------

export function* dashboardOpenWorker() {
  // analytics
  track.dashboardOpened()

  yield take(GET_CURRENT_EVENT_SUCCESS)

  const state = yield select()

  // open the Event Details section of the Dashboard if one or more of the
  //  following conditions holds true:
  //    1. no event date
  //    2. no event name
  //    3. no event type
  // othewise, close it
  if (events.isMissingDetails(state)) yield put(openEventDetails())

  // close the Mobile Menu if it's open
  if (mobileMenu.isOpen(state)) yield put({type: CLOSE_MOBILE_MENU})
}

export function* dashboardOpenSaga() {
  yield takeEvery(OPEN_DASHBOARD, dashboardOpenWorker)
}

// Close
// ----------------------------------------------------------------------------

export function* dashboardCloseWorker() {
  // analytics
  track.dashboardClosed()

  const isEventDetailsExpanded = yield select(dashboard.eventDetailsExpanded)

  // close any open editing windows
  yield put(leaveEventDetailEditMode())

  //  close event details if expanded
  if (isEventDetailsExpanded) yield put(closeEventDetails())
}

export function* dashboardCloseSaga() {
  yield takeEvery(CLOSE_DASHBOARD, dashboardCloseWorker)
}

// Enter Event Details Edit Mode
// ----------------------------------------------------------------------------

function enterEventDetailsEditModeWorker() {
  // add class to html tag
  html.classList.add('is--event-details-edited')
}

export function* enterEventDetailsEditModeSaga() {
  yield takeEvery(ENTER_EVENT_DETAIL_EDIT_MODE, enterEventDetailsEditModeWorker)
}

// Leave Event Details Edit Mode
// ----------------------------------------------------------------------------

function leaveEventDetailsEditModeWorker() {
  // remove class from html tag
  html.classList.remove('is--event-details-edited')
}

export function* leaveEventDetailsEditModeSaga() {
  yield takeEvery(LEAVE_EVENT_DETAIL_EDIT_MODE, leaveEventDetailsEditModeWorker)
}

// Validate Dashboard Content on CTA click
// ----------------------------------------------------------------------------

export function* validateDashboardFailureWorker() {
  const allLooks = yield select(looks.looks)
  const currentDate = yield select(events.currentDate)
  const getLooksMissingProducts = yield select(looks.getLooksMissingProducts)
  const getLooksMissingWearerAssignment = yield select(cross.getLooksMissingWearerAssignment)
  const errors = yield call(getErrors, getLooksMissingProducts, getLooksMissingWearerAssignment)

  yield call(
    track.dashboardErrorBlockedCustomer,
    formatDateString(currentDate, 'MMM D, YYYY'),
    errors,
  )
  yield call(scrollToFirstLookError, allLooks, errors)
}

export function* validateDashboardFailureSaga() {
  yield takeLatest(VALIDATE_DASHBOARD_FAILURE, validateDashboardFailureWorker)
}

export function* validateDashboardWorker({payload}) {
  const {clickEvent} = payload
  clickEvent.persist()
  yield put(validateDashboardPending())

  const anyLookIsMissingProducts = yield select(looks.anyLookIsMissingProducts)
  const anyLookIsMissingWearerAssignment = yield select(cross.anyLookIsMissingWearerAssignment)

  if (anyLookIsMissingProducts || anyLookIsMissingWearerAssignment) {
    // we found an error
    yield put(validateDashboardFailure())
    clickEvent.preventDefault()
  } else {
    yield put(validateDashboardSuccess())
    yield call(track.dashboardContinueClicked, {toPage: clickEvent.target.href})
    if (clickEvent.target.pathname === URLS.CHECKOUT) {
      // the event owner has no looks for himself
      yield put(proceedToCheckout())
    }
  }
}

export function* validateDashboardSaga() {
  yield takeLatest(VALIDATE_DASHBOARD, validateDashboardWorker)
}

// Get Checkout Message for Dashboard Banner
// ----------------------------------------------------------------------------

export function* getCheckoutMessageWorker() {
  const eventId = yield select(events.currentId)

  if (!eventId) {
    return
  }

  try {
    yield put(getCheckoutMessagePending())

    const {json, status} = yield call(api.getCheckoutMessage, eventId)

    if (isBadResponse(status)) {
      throw new Error('Could not get checkout message. Unexpected error.')
    }

    yield put(getCheckoutMessageSuccess(json))
  } catch (e) {
    yield put(getCheckoutMessageFailure(e))
  }
}

export function* getCheckoutMessageSaga() {
  yield takeEvery(
    [UPDATE_CURRENT_EVENT, GET_CURRENT_EVENT_SUCCESS, ADD_PARTICIPANT, REMOVE_PARTICIPANT],
    getCheckoutMessageWorker,
  )
}

export function* closeSlidingModalWorker() {
  yield put(closeSlidingModal())
}

export function* closeSlidingModalSaga() {
  yield takeLatest(EDIT_LOOK_NAME_SUCCESS, closeSlidingModalWorker)
}
